/**
 * `useCopyMoveAction` — the shared ORCHESTRATOR for the CCM "smoothest UX"
 * copy/move (handoff `ccm-lexide-copy-move-smooth-ui-handoff-2026-06-30.md`).
 *
 * Both fast paths — the drag-to-destination overlay (§1.1) and the type-ahead
 * "Send to…" picker (§1.2) — AND the H#4 fallback dialog funnel a completed
 * pick through here so the post-op behavior is identical no matter how the
 * lawyer triggered it:
 *
 *   1. Runs the op via `useResourceCopyMove` (the H#4 mutation — reused
 *      as-is; it owns the `{from,to}` envelope + the dest/source tree
 *      invalidation).
 *   2. Records the destination in the recent-destinations store (§1.3).
 *   3. Drops a success toast. For a MOVE the toast carries an **Undo** action
 *      (§1.4) that fires the inverted move (`invertMove`). For a COPY there is
 *      NO Undo — there is no file-delete RPC, so undoing a copy is impossible
 *      today; the toast is a plain confirmation (handoff §4 — flag, don't
 *      fake). The undo move ITSELF re-records + toasts (without a further
 *      undo, to avoid an undo/redo ping-pong) and surfaces its own errors.
 *   4. On error surfaces a stable result the caller can render (the picker /
 *      overlay translate the message).
 *
 * Why a hook and not inline in each surface: the toast + recent + undo wiring
 * is non-trivial and MUST be identical across the drag overlay, the picker,
 * and the dialog — one definition keeps them from drifting.
 */

import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  useResourceCopyMove,
  ResourceCopyMoveError,
  type ResourceCopyMoveInput,
} from "@/hooks/useResourceCopyMove";
import { useToastStore } from "@/stores/useToastStore";
import { useRecentDestinationsStore } from "@/stores/useRecentDestinationsStore";
import { invertMove } from "@/lib/copy-move-collision";
import type { CopyMoveTarget } from "@/lib/copy-move-targets";

export interface RunCopyMoveArgs {
  mode: "copy" | "move";
  /** Resource path, workspace-relative (e.g. "sources/contrato.pdf"). */
  path: string;
  /** Display basename for the toast / undo messaging. */
  name: string;
  /** The destination target (from the picker, a chip, or a drop zone). */
  target: CopyMoveTarget;
  /** The source scope (case full triple, or org). */
  source: ResourceCopyMoveInput["source"];
}

export interface RunCopyMoveResult {
  ok: boolean;
  /** A translated, ready-to-render error string when `ok` is false. */
  error?: string;
}

export interface UseCopyMoveAction {
  /** Fire a copy/move; resolves with `{ok}` (never rejects). On success it
   *  has already recorded the recent dest + dropped the toast. */
  run: (args: RunCopyMoveArgs) => Promise<RunCopyMoveResult>;
  /** Whether a copy/move is currently in flight (drives the spinner). */
  isPending: boolean;
}

export function useCopyMoveAction(): UseCopyMoveAction {
  const { t } = useTranslation();
  const mutation = useResourceCopyMove();
  const addToast = useToastStore((s) => s.addToast);
  const recordDestination = useRecentDestinationsStore(
    (s) => s.recordDestination,
  );

  const translateError = useCallback(
    (err: unknown): string => {
      if (err instanceof ResourceCopyMoveError) {
        return t("resourceCopyMove.noGateway");
      }
      return err instanceof Error && err.message
        ? err.message
        : t("resourceCopyMove.failed");
    },
    [t],
  );

  const run = useCallback(
    async (args: RunCopyMoveArgs): Promise<RunCopyMoveResult> => {
      // `allowUndo` is true for the FIRST move; false for an undo's own move
      // (so the toast doesn't offer to undo the undo — ping-pong guard).
      const exec = async (
        input: ResourceCopyMoveInput,
        toastTarget: CopyMoveTarget,
        allowUndo: boolean,
      ): Promise<RunCopyMoveResult> => {
        try {
          await mutation.mutateAsync(input);
          // Record the destination for the recent chips (§1.3). Skip
          // recording an undo's reversing destination — that's the place the
          // lawyer just came FROM, not a forward "recent send-to".
          if (allowUndo) recordDestination(toastTarget);

          const isMove = input.mode === "move";
          const text = isMove
            ? t("resourceCopyMove.toastMoved", { dest: toastTarget.title })
            : t("resourceCopyMove.toastCopied", { dest: toastTarget.title });

          // Undo is offered ONLY on a move (copy-undo needs a delete RPC that
          // doesn't exist — handoff §4) AND only on the first move (not an
          // undo's own move).
          const action =
            isMove && allowUndo
              ? {
                  label: t("resourceCopyMove.undo"),
                  onAction: () => {
                    const undoInput = invertMove({
                      path: input.path,
                      originalSource: input.source,
                      originalTarget: toastTarget,
                    });
                    // The undo's display target = the original source scope.
                    // Fire it without offering a further undo.
                    void exec(undoInput, undoInput.target, false);
                  },
                }
              : undefined;

          addToast({
            text,
            kind: "success",
            autoDismissMs: action ? 8000 : 4000,
            action,
          });
          return { ok: true };
        } catch (err) {
          // An undo failure can't surface in the dialog (the toast is gone) —
          // drop an error toast for it; the forward op returns the error so
          // the picker/overlay can render it inline.
          if (!allowUndo) {
            addToast({
              text: t("resourceCopyMove.undoFailed"),
              kind: "error",
              autoDismissMs: 6000,
            });
          }
          return { ok: false, error: translateError(err) };
        }
      };

      const input: ResourceCopyMoveInput = {
        mode: args.mode,
        path: args.path,
        target: args.target,
        source: args.source,
      };
      return exec(input, args.target, true);
    },
    [mutation, addToast, recordDestination, t, translateError],
  );

  return { run, isPending: mutation.isPending };
}
